import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  ColumnDef as TanStackColumnDef,
  useReactTable,
} from "@tanstack/react-table";
import Pagination from "components/Pagination/Pagination";
import React, { Fragment } from "react";
import { Row, Table } from "react-bootstrap";
import { filterPopoverStyles } from "Constants/Style";
import DateRangeFilterPopover from "./DateRangeFilterPopover";
import FilterPopover from "./FilterPopover";
import NoDataFound from "../NoDataFound";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import { Alert } from "react-bootstrap";

type FilterType = "dropdown" | "text" | "date" | "boolean";

export type CustomColumnDef<TData> = TanStackColumnDef<TData> & {
  filterType?: FilterType;
  filterOptions?: string[];
  filterFetchUrl?: string;

  //*// New changes
  Rnet?: string;
};

interface TableContainerProps<TData = any> {
  // columns: CustomColumnDef<TData>[];
  columns: any;
  data: TData[];
  totalRecords: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  colValue: any;
  isBordered?: boolean;
  isPagination?: boolean;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  manualSorting: boolean;
  isGlobalFilter?: boolean;
  SearchPlaceholder?: string;
  loading?: boolean;
  error?: string | null | any;
}

const DashboardTableContainer = ({
  columns,
  data,
  totalRecords,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  columnFilters,
  setColumnFilters,
  sorting,
  onSortingChange,
  manualSorting = true,
  isBordered = false,
  isPagination = true,
  colValue,
  loading = false,
  error = null,
}: TableContainerProps) => {
  const handleColumnFilterChange: OnChangeFn<ColumnFiltersState> = (
    updaterOrValue
  ) => {
    if (!setColumnFilters) return;
    setColumnFilters((prev: any) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue
    );
  };

  // console.log("data: ", data)
  // console.log("colValue: ", colValue)
  // console.log("columnFilters: ", columnFilters)
  // console.log("sorting: ", sorting)
  // console.log("pageIndex: ", pageIndex)
  // console.log("pageSize: ", pageSize)
  // console.log("totalRecords: ", totalRecords)
  // console.log("onPageChange: ", onPageChange)
  // console.log("onPageSizeChange: ", onPageSizeChange)
  // console.log("manualSorting: ", manualSorting)
  // console.log("isBordered: ", isBordered)
  // console.log("isPagination: ", isPagination)

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualFiltering: true,
    manualSorting,
    pageCount: Math.ceil(totalRecords / pageSize),
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
      columnFilters,
    },
    onSortingChange,
    onColumnFiltersChange: handleColumnFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: !manualSorting ? getSortedRowModel() : undefined,
  });

  const { getHeaderGroups, getRowModel, getCanPreviousPage, getCanNextPage } =
    table;

  return (
    <Fragment>
      {/* Sticky column styles */}
      <style>
        {`
        .sticky-column {
          position: sticky;
          right: 0;
          z-index: 10;
          background-color: #fff !important;
          border-left: 1px solid #dee2e6;
        }

        thead .sticky-column {
          background-color: #EEF0F7 !important;
        }

        .sticky-action {
          box-shadow: -4px 0 6px rgba(0, 0, 0, 0.1);
        }

        .table td,
        .table th {
          background-clip: padding-box;
        }
      `}
      </style>

      <div className="table-responsive">
        <Table
          className={`table table-centered align-middle table-custom-effect table-nowrap mb-0 ${
            isBordered ? "table-bordered" : ""
          }`}
          bordered={isBordered}
        >
          <thead className="table-light">
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isLastColumn = index === headerGroup.headers.length - 1;

                  // Check for stickyRight in meta or fallback to header text check for backwards compatibility
                  const hasStickyRight = (header.column.columnDef.meta as any)?.stickyRight === true;
                  const isActionColumn =
                    header.column.columnDef.header?.toString().toLowerCase() ===
                    "action" ||
                    header.column.columnDef.header?.toString().toLowerCase() ===
                    "actions";

                  const shouldBeSticky = hasStickyRight || (isLastColumn && isActionColumn);

                  const isSorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  const sortIcon =
                    isSorted === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : isSorted === "desc" ? (
                      <ArrowDownwardIcon fontSize="small" />
                    ) : (
                      <UnfoldMoreIcon fontSize="small" />
                    );

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`align-middle ${
                        shouldBeSticky
                          ? "sticky-column sticky-action "
                          : ""
                      }`}
                    >
                      {!header.isPlaceholder && (
                        <div
                          className="d-flex  align-items-center"
                          style={{ gap: 4 }}
                        >
                          <div
                            className="d-flex align-items-center"
                            style={{
                              gap: 4,
                              cursor: canSort ? "pointer" : "default",
                            }}
                            onClick={
                              canSort
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                          >
                            <span>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>
                            {canSort && sortIcon}
                          </div>

                          {header.column.getCanFilter() ? (
                            (header.column.columnDef as CustomColumnDef<any>)
                              .filterType === "date" ? (
                              <DateRangeFilterPopover
                                column={header.column}
                                setColValue={colValue}
                                iconColor={
                                  header.column.getFilterValue()
                                    ? filterPopoverStyles.primaryColor
                                    : "inherit"
                                }
                              />
                            ) : (
                              <FilterPopover
                                column={header.column}
                                columnDef={
                                  header.column
                                    .columnDef as CustomColumnDef<any>
                                }
                                columnFilters={columnFilters}
                                setColumnFilters={setColumnFilters}
                                setColValue={colValue}
                                Rnet={
                                  (
                                    header.column
                                      .columnDef as CustomColumnDef<any>
                                  ).Rnet
                                }
                                iconColor={
                                  header.column.getFilterValue()
                                    ? filterPopoverStyles.primaryColor
                                    : "inherit"
                                }
                              />
                            )
                          ) : null}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 0, border: 'none' }}>
                  <LoadingSpinner themeColor="#3969E7" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: '1rem', border: 'none' }}>
                  <Alert variant="danger" className="my-0">
                    <strong>Error:</strong>{" "}
                    {typeof error === "string"
                      ? error
                      : error?.error ||
                        error?.message ||
                        error?.response?.data?.error ||
                        error?.response?.data?.message ||
                        "An error occurred"}
                  </Alert>
                </td>
              </tr>
            ) : getRowModel().rows.length === 0 ? (
              <NoDataFound colSpan={columns.length} />
            ) : (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, index) => {
                    const isLastColumn =
                      index === row.getVisibleCells().length - 1;

                    // Check for stickyRight in meta or fallback to header text check for backwards compatibility
                    const hasStickyRight = (cell.column.columnDef.meta as any)?.stickyRight === true;
                    const isActionColumn =
                      cell.column.columnDef.header?.toString().toLowerCase() ===
                      "action" ||
                      cell.column.columnDef.header?.toString().toLowerCase() ===
                      "actions";

                    const shouldBeSticky = hasStickyRight || (isLastColumn && isActionColumn);

                    return (
                      <td
                        key={cell.id}
                        className={
                          shouldBeSticky
                            ? "sticky-column sticky-action"
                            : ""
                        }
                        style={{
                          maxWidth: cell.column.columnDef.maxSize ?? "none",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className="align-items-center mt-0 py-3 gy-2 mx-0">
          <Pagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Row>
      )}
    </Fragment>
  );
};

export default DashboardTableContainer;

